/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import { FormattedMessage } from 'react-intl';
import Button from 'components/ChoreoSystem/Button/Button';
import ErrorCard from 'components/ChoreoSystem/ErrorCards/ErrorCard';
import ErrorCodeMessage from 'components/ChoreoSystem/ErrorCards/ErrorCodeMessage';
import { ReactComponent as UnauthorizedAccess } from 'components/ChoreoSystem/Images/ErrorPages/UnauthorizedAccess.svg';
import { ReactComponent as OopsEmbarrassingImg } from 'components/ChoreoSystem/Images/ErrorPages/OppsEmbarrassing.svg';
import { ReactComponent as AuthError } from 'components/ChoreoSystem/Images/ErrorPages/AuthenticationError.svg';
import { ReactComponent as SessionTimeoutImg } from 'components/ChoreoSystem/Images/ErrorPages/SessionTimeout.svg';
import { ReactComponent as NotFoundImg } from 'components/ChoreoSystem/Images/ErrorPages/404.svg';
import { useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import useStyles from './styles';

interface CustomErrorPageProps {
  errorCode?: string;
  errorMessage?: string;
  customAction?: React.ReactNode;
  customDescription?: React.ReactNode;
  customTitle?: React.ReactNode;
  type?:
    | 'AuthError'
    | 'UnauthorizedError'
    | 'NotFoundError'
    | 'SessionTimeoutError'
    | 'GenericError'
    | 'CannotObserverError';
}

interface ErrorContent {
  title: React.ReactNode;
  description: React.ReactNode;
  image: React.ReactNode;
  action: React.ReactNode;
}

export default function CustomErrorPage(props: CustomErrorPageProps) {
  const {
    errorCode,
    errorMessage,
    type,
    customAction,
    customDescription,
    customTitle,
  } = props;
  const [errorContent, setErrorContent] = useState<ErrorContent>({
    title: '',
    description: '',
    image: '',
    action: '',
  });
  const classes = useStyles();
  const testId = 'custom-error';

  const RefreshButton = (
    <Button
      onClick={() => {
        window.location.reload();
      }}
      testId="refresh-button"
    >
      <FormattedMessage
        defaultMessage="Refresh"
        id="modules.deploy.deployConsolePanelV2.BuildDeployResult.refreshButton"
      />
    </Button>
  );

  const getErrorContent = () => {
    let title: React.ReactNode = '';
    let description: React.ReactNode = '';
    let image: React.ReactNode = '';
    let action: React.ReactNode = '';
    switch (type) {
      case 'AuthError':
        title = (
          <FormattedMessage
            id="components.ErrorPages.CustomErrorPage.AuthError.title"
            defaultMessage="Authentication Error"
          />
        );
        description = (
          <FormattedMessage
            id="components.ErrorPages.CustomErrorPage.AuthError.description"
            defaultMessage="Something went wrong during the authentication process. Please try signing in again."
          />
        );
        image = <AuthError />;
        break;
      case 'UnauthorizedError':
        title = (
          <FormattedMessage
            id="components.ErrorPages.CustomErrorPage.UnauthorizedError.title"
            defaultMessage="Unauthorized access page"
          />
        );
        description = (
          <FormattedMessage
            id="components.ErrorPages.CustomErrorPage.UnauthorizedError.description"
            defaultMessage="Something went wrong. Refresh and try again"
          />
        );
        image = <UnauthorizedAccess />;
        break;
      case 'NotFoundError':
        title = (
          <FormattedMessage
            id="components.ErrorPages.CustomErrorPage.NotFoundError.title"
            defaultMessage="Page not found!"
          />
        );
        description = (
          <FormattedMessage
            id="components.ErrorPages.CustomErrorPage.NotFoundError.description"
            defaultMessage="The requested page could not be found."
          />
        );
        image = <NotFoundImg />;
        break;
      case 'SessionTimeoutError':
        title = (
          <FormattedMessage
            id="components.ErrorPages.CustomErrorPage.SessionTimeoutError.title"
            defaultMessage="Oops! Your session has timed out!"
          />
        );
        description = (
          <FormattedMessage
            id="components.ErrorPages.CustomErrorPage.SessionTimeoutError.description"
            defaultMessage="Your session has timed out. Refresh and try again"
          />
        );
        image = <SessionTimeoutImg />;
        action = RefreshButton;
        break;
      case 'GenericError':
      default:
        title = (
          <FormattedMessage
            id="components.ErrorPages.CustomErrorPage.GenericError.title"
            defaultMessage="Oops! This is embarrassing"
          />
        );
        description = (
          <FormattedMessage
            id="components.ErrorPages.CustomErrorPage.GenericError.description"
            defaultMessage="Something went wrong! Refresh and try again."
          />
        );
        image = <OopsEmbarrassingImg />;
        action = RefreshButton;
        break;
    }
    setErrorContent({ title, description, image, action });
  };

  useEffect(() => {
    getErrorContent();
  }, [type]);

  return (
    <Box
      className={classes.container}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <ErrorCard
        title={customTitle || errorContent.title}
        image={errorContent.image}
        description={customDescription || errorContent.description}
        actions={customAction || errorContent.action}
        testId={testId}
      >
        {errorCode && errorMessage && (
          <ErrorCodeMessage
            code={errorCode}
            message={errorMessage}
            testId={testId}
          />
        )}
      </ErrorCard>
    </Box>
  );
}
